import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withGetStringAudio } from 'components/hocs';
import { toggleAudio } from 'actions/audioActions';
import { audioURL, imagesURL } from 'config';
import { formatHtmlForPlay, getTranslateFunc } from 'utils';
import isMobileAppRuntime from 'utils/isMobileAppRuntime';
import { isOnline, subscribeOfflineState } from 'mobile-modules/offline/state';
import GameModal from '../GameModal/GameModal';
import classes from './DesktopAssessmentReportModal.module.scss';

const REPORT_TITLE_STRING_NUMBER = 3709;
const USER_STRING_NUMBER = 3711;
const DATE_STRING_NUMBER = 1001;
const EXPLANATORY_TEXT_STRING_NUMBER = 3861;
const FULL_PRODUCT_EXPLANATORY_TEXT_STRING_NUMBER = 3862;
const AGES_STRING_NUMBER = 3716;
const LANGUAGE_STRING_NUMBER = 236;
const SKILL_RATINGS_STRING_NUMBER = 3713;
const ACTIVITIES_COMPLETED_STRING_NUMBER = 3710;
const INTERESTS_STRING_NUMBER = 3717;
const GET_CUSTOM_CONTENT_STRING_NUMBER = 3721;
const EXPLANATORY_TEXT_FALLBACK = 'This is your status. You need to do at least one activity for each skill. When you have done all the activities you can in each skill, you can choose to get a fully custom product based on your reading skills and interests.';
const FULL_PRODUCT_EXPLANATORY_TEXT_FALLBACK = 'These are suggested starting levels based on your Placement Activities. Your complete product is already available, so you can use these recommendations as a starting point while still having access to all content in your product.';
const CUSTOM_READINGS_TITLE_STRING_NUMBER = 3858;
const STANDARD_READINGS_TITLE_STRING_NUMBER = 3859;
const ADDITIONAL_READINGS_TITLE_STRING_NUMBER = 3860;
const ADDITIONAL_READINGS_TEXT_STRING_NUMBER = 3863;
const STANDARD_READINGS_TEXT_STRING_NUMBER = 3864;
const ADDITIONAL_READINGS_TEXT = 'You can select any or all of the additional readings below or just get the standard readings.';
const STANDARD_READINGS_TEXT = "Together We Learn {{age}} comes with over 250 readings. All the readings are children's picture books. The readings include both fiction and non-fiction picture books. The fiction stories are all illustrated, many with Pixar graphics. Many are about children and their families, others are about animals, sports, and other topics of interest to young children. The non-fiction e-books are also highly illustrated and include topics such as pets, birds, horses, fire engines, construction equipment, and many more.";

const REPORT_AGE_LABELS = {
  age_4_8: { stringNumber: 3809, fallback: 'Ages 4–8' },
  age_9_13: { stringNumber: 3810, fallback: 'Ages 9–13' },
  age_14_18: { stringNumber: 3811, fallback: 'Ages 14–18' },
  adult: { stringNumber: 3812, fallback: 'Adults' },
};


const SUBJECT_STRING_NUMBERS = {
  phonics: 147,
  reading: 151,
  vocabulary: 157,
  writing: 161,
};

const SUBJECT_FALLBACKS = {
  phonics: 'Phonics',
  reading: 'Reading',
  vocabulary: 'Vocabulary',
  writing: 'Writing',
};

const INTERESTS_BY_LANGUAGE_AND_AGE = {
  en: {
    age_4_8: [
      { key: 'animals', stringNumber: 843, label: 'Animals' },
      { key: 'bible_stories', stringNumber: 3718, label: 'Bible Stories' },
      { key: 'black_history', stringNumber: 3719, label: 'Black History' },
    ],
    age_9_13: [
      { key: 'bible_stories', stringNumber: 3718, label: 'Bible Stories' },
      { key: 'black_history', stringNumber: 3719, label: 'Black History' },
    ],
    age_14_18: [
      { key: 'civics', stringNumber: 3852, label: 'Civics' },
      { key: 'black_history', stringNumber: 3719, label: 'Black History' },
    ],
    adult: [
      { key: 'civics', stringNumber: 3852, label: 'Civics' },
      { key: 'black_history', stringNumber: 3719, label: 'Black History' },
    ],
  },
  es: {
    age_4_8: [
      { key: 'bible_stories', stringNumber: 3718, label: 'Bible Stories' },
      { key: 'animals', stringNumber: 843, label: 'Animals' },
    ],
    age_9_13: [
      { key: 'bible_stories', stringNumber: 3718, label: 'Bible Stories' },
    ],
  },
  // Per the reading-interests matrix, Vietnamese and all other languages
  // expose Animals only for Ages 4–8. No other combinations are specified.
  other: {
    age_4_8: [{ key: 'animals', stringNumber: 843, label: 'Animals' }],
  },
};

const LANGUAGE_LABELS = {
  en: 'English',
  eng: 'English',
  english: 'English',
  es: 'Spanish',
  spa: 'Spanish',
  spanish: 'Spanish',
  vm: 'Vietnamese',
  vi: 'Vietnamese',
  vietnamese: 'Vietnamese',
};

function getReportFromAssessment(assessment) {
  return assessment?.reportSnapshot?.report || assessment?.attempt?.summary?.report || null;
}

function makeTranslator(translate) {
  return (stringNumber, fallback) => {
    if (!stringNumber || typeof translate !== 'function') return fallback;
    return translate(stringNumber, fallback, true);
  };
}

function normalizeAgeBandLabel(value) {
  switch (value) {
    case 'age_4_8':
    case 'early_literacy':
      return 'Ages 4-8';
    case 'age_9_13':
      return 'Ages 9-13';
    case 'age_14_18':
      return 'Ages 14-18';
    case 'adult':
    case 'older_literacy':
      return 'Adults';
    case 'prek_3':
      return 'PreK';
    default:
      return null;
  }
}

function getAgeBandKey(assessment, report) {
  return report?.ageBand || assessment?.attempt?.ageBand || report?.assessmentProfile || assessment?.attempt?.assessmentProfile || null;
}

function getAgeBandDisplay(assessment, report) {
  const direct = normalizeAgeBandLabel(getAgeBandKey(assessment, report));
  if (direct) return direct;

  const text = [
    assessment?.freeTrialGroup?.name,
    assessment?.freeTrialGroup?.__assessmentCategoryTitle,
    assessment?.attempt?.solutionName,
    assessment?.attempt?.solutionDisplayName,
  ].filter(Boolean).join(' ');

  const match = text.match(/(?:ages?\s*)?(4\s*[-–—]\s*8|9\s*[-–—]\s*13|14\s*[-–—]\s*18)/i);
  return match ? `Ages ${match[1].replace(/\s+/g, '')}` : '—';
}

function getLanguageKey(report, assessment) {
  const rawLanguage = report?.language
    || assessment?.attempt?.instructionLanguage
    || assessment?.attempt?.language
    || assessment?.attempt?.solutionLocale
    || '';
  return String(rawLanguage || '').trim().toLowerCase();
}

function getLanguageDisplay(report, assessment) {
  const rawLanguage = report?.language
    || assessment?.attempt?.instructionLanguage
    || assessment?.attempt?.language
    || assessment?.attempt?.solutionLocale
    || '';
  const key = getLanguageKey(report, assessment);
  return LANGUAGE_LABELS[key] || rawLanguage || '—';
}

function getGeneratedDate(report, assessment) {
  const rawDate = report?.generatedAt || assessment?.attempt?.summary?.generatedAt || assessment?.attempt?.updatedAt || null;
  if (!rawDate) return '—';

  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}

function hasLevel(value) {
  return value !== null && value !== undefined && value !== '';
}

function getProgressLine(progress, subject) {
  const completedCount = progress?.[subject]?.completedCount ?? 0;
  const requiredCount = progress?.[subject]?.requiredCount ?? 0;
  return `${completedCount}/${requiredCount}`;
}

function getAvailableInterests({ ageBandKey, languageKey, readingInterests = [] }) {
  const code = String(languageKey || '').trim().toLowerCase();
  const normalizedLanguage = ['en', 'eng', 'english'].includes(code)
    ? 'en'
    : (['es', 'spa', 'spanish'].includes(code) ? 'es' : 'other');
  const normalizedAgeBand = ageBandKey === 'early_literacy'
    ? 'age_4_8'
    : (ageBandKey === 'older_literacy' ? 'adult' : ageBandKey);
  const allowed = INTERESTS_BY_LANGUAGE_AND_AGE[normalizedLanguage][normalizedAgeBand] || [];
  const configured = new Map((readingInterests || []).filter(item => item?.key).map(item => [item.key, item]));

  // The configured String IDs can override labels, but must not add interests
  // outside the approved language/age matrix (including the Vietnamese case).
  return allowed.map(item => ({
    ...item,
    stringNumber: configured.get(item.key)?.stringId || item.stringNumber,
  }));
}

// The rich-text editor can wrap {{age}} in a tiny inline font-size.
// Remove formatting only when the inline element contains the age token alone;
// keep every other piece of the authored String 3864 HTML unchanged.
function unwrapAgeOnlyFormatting(html) {
  const ageOnlyTag = /<(span|font|small|sup|sub|strong|b|em|i)(?:\s[^>]*)?>\s*(\{\{\s*(?:age|ages|ageBand|ageRange)\s*\}\})\s*<\/\1>/gi;
  let result = html;
  for (let depth = 0; depth < 8; depth += 1) {
    const next = result.replace(ageOnlyTag, '$2');
    if (next === result) break;
    result = next;
  }
  return result;
}

function getStandardReadingsText(assessment, report, translate) {
  const rawAgeBand = getAgeBandKey(assessment, report);
  const ageBandKey = rawAgeBand === 'early_literacy'
    ? 'age_4_8'
    : (rawAgeBand === 'older_literacy' ? 'adult' : rawAgeBand);
  const ageConfig = REPORT_AGE_LABELS[ageBandKey];
  const ageFallback = getAgeBandDisplay(assessment, report);
  const localizedAge = ageConfig && typeof translate === 'function'
    ? translate(ageConfig.stringNumber, ageConfig.fallback, true)
    : null;
  const ageLabel = typeof localizedAge === 'string' && localizedAge.trim()
    ? localizedAge
    : ageFallback === '—' ? '' : ageFallback;
  const localizedText = typeof translate === 'function'
    ? translate(STANDARD_READINGS_TEXT_STRING_NUMBER, STANDARD_READINGS_TEXT, false, true)
    : null;
  const template = typeof localizedText === 'string' && localizedText.trim()
    ? localizedText
    : STANDARD_READINGS_TEXT;

  // Age String labels must not inject their own rich-text font sizing.
  const plainAgeLabel = ageLabel.replace(/<[^>]*>/g, '');

  // Prefer {{age}} in string 3864; support the existing literal age in stored text.
  return unwrapAgeOnlyFormatting(template)
    .replace(/\{\{\s*(?:age|ages|ageBand|ageRange)\s*\}\}/gi, plainAgeLabel)
    .replace(/\bAges?\s*4\s*[-–—]\s*8\b/gi, plainAgeLabel);
}

function resolveReportStringAudioUrl({
  stringNumber,
  getStringAudio,
  strings,
  languageKey,
  solutionLocale,
}) {
  const resolvedAudio = typeof getStringAudio === 'function'
    ? getStringAudio(stringNumber, true)
    : null;

  if (resolvedAudio?.audioUrl) {
    return resolvedAudio.audioUrl;
  }

  if (resolvedAudio?.audio && resolvedAudio?.path) {
    return `${resolvedAudio.path}${resolvedAudio.audio}`;
  }

  const string = (strings || []).find((item) => Number(item?.stringNumber) === Number(stringNumber));
  const requestedCode = String(languageKey || solutionLocale?.code || '').toLowerCase();
  const localeCode = requestedCode.startsWith('es') ? 'es' : 'en';
  const localizedValue = string?.data?.[localeCode] || string?.data?.en || null;
  const filename = localizedValue?.stringAudio || localizedValue?.audio || localizedValue?.current || null;

  if (!filename) {
    return null;
  }

  const localeFolder = localeCode === 'es' ? 'Spanish' : 'English';
  return `${audioURL}/Generic/Strings/${localeFolder}/${filename}`;
}

const DesktopAssessmentReportModal = ({ assessment, open, onClose, onGetCustomContent, translate, learner, strings, solutionLocale, getStringAudio, audioSrc, isPlaying, dispatchToggleAudio, subjectStringIds, readingInterests, suggestionsOnly, fullProductReportStrings }) => {
  const mobileRuntime = isMobileAppRuntime();
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [view, setView] = useState('report');
  const [mobileOnline, setMobileOnline] = useState(() => !mobileRuntime || isOnline());

  useEffect(() => {
    if (!open) {
      setSelectedInterests([]);
      setView('report');
    }
  }, [open]);

  useEffect(() => {
    if (!mobileRuntime) return undefined;

    setMobileOnline(isOnline());
    return subscribeOfflineState((offlineState) => {
      setMobileOnline(offlineState.connected !== false);
    });
  }, [mobileRuntime]);

  if (!open) return null;

  const t = makeTranslator(translate);
  const explanatoryStringNumber = suggestionsOnly
    ? FULL_PRODUCT_EXPLANATORY_TEXT_STRING_NUMBER
    : EXPLANATORY_TEXT_STRING_NUMBER;
  const explanatoryFallback = suggestionsOnly
    ? FULL_PRODUCT_EXPLANATORY_TEXT_FALLBACK
    : EXPLANATORY_TEXT_FALLBACK;
  const explanatoryHtml = formatHtmlForPlay({
    html: explanatoryStringNumber > 0 && typeof translate === 'function'
      ? translate(explanatoryStringNumber, explanatoryFallback, false, true)
      : explanatoryFallback,
  });
  const report = getReportFromAssessment(assessment);
  const levels = report?.levels || assessment?.attempt?.summary?.levels || {};
  const progress = report?.progress || assessment?.attempt?.summary?.completion || {};
  const ageBandKey = getAgeBandKey(assessment, report);
  const languageKey = getLanguageKey(report, assessment);
  const explanatoryAudioUrl = explanatoryStringNumber > 0
    ? resolveReportStringAudioUrl({
        stringNumber: explanatoryStringNumber,
        getStringAudio,
        strings,
        languageKey,
        solutionLocale,
      })
    : null;
  const explanatoryAudioIcon = `${imagesURL}/Images/Audio/${audioSrc === explanatoryAudioUrl && isPlaying ? 'audiostop' : 'audio_icon1'}.png`;
  const interests = getAvailableInterests({ ageBandKey, languageKey, readingInterests });
  const isMobileAssessment = assessment?.mode === 'mobile' || assessment?.attempt?.mode === 'mobile';
  const allSkillsReady = ['phonics', 'reading', 'vocabulary', 'writing'].every((subject) => hasLevel(levels[subject]));
  const hasGeneratedCustomContent = Boolean(assessment?.generatedCustomContentLevel?._id);
  const suggestionsSaved = Boolean(
    suggestionsOnly && assessment?.attempt?.status === 'finalized'
  );
  const canShowCustomContentButton = isMobileAssessment
    && typeof onGetCustomContent === 'function'
    && !hasGeneratedCustomContent
    && !suggestionsSaved;
  const canGetCustomContent = canShowCustomContentButton && allSkillsReady && (!mobileRuntime || mobileOnline);
  const customReadingsOpen = mobileRuntime && !suggestionsOnly && view === 'custom-readings';
  const standardReadingsHtml = formatHtmlForPlay({
    html: getStandardReadingsText(assessment, report, translate),
  });

  const buttons = [
    ...(canShowCustomContentButton ? [{
      title: t(
        suggestionsOnly && Number(fullProductReportStrings?.createButtonStringId || 0) > 0
          ? Number(fullProductReportStrings.createButtonStringId)
          : GET_CUSTOM_CONTENT_STRING_NUMBER,
        'Get Your Custom Content'
      ),
      clickHandler: suggestionsOnly
        ? () => onGetCustomContent([])
        : (mobileRuntime
          ? (customReadingsOpen
            ? () => onGetCustomContent(selectedInterests)
            : () => setView('custom-readings'))
          : () => onGetCustomContent(selectedInterests)),
      disabled: !canGetCustomContent,
    }] : []),
    {
      title: t(80, 'Close'),
      clickHandler: onClose,
    },
  ];

  const subjectRows = ['phonics', 'vocabulary', 'reading', 'writing'];

  return (
    <GameModal
      title={customReadingsOpen
        ? t(CUSTOM_READINGS_TITLE_STRING_NUMBER, 'Custom Readings')
        : t(report?.titleStringNumber || REPORT_TITLE_STRING_NUMBER, report?.title || 'Customized Family Learning Report')}
      buttons={buttons}
      centerButtons
      outerClassnames={[classes.reportModal, ...(mobileRuntime ? [classes.mobileReportModal] : [])]}
      innerClassnames={[classes.reportModalInner]}
      contentClassnames={[classes.reportModalContent]}
    >
      {customReadingsOpen ? (
        <div className={classes.customReadingsBody}>
          <section className={classes.customReadingsColumn}>
            <h3>{t(STANDARD_READINGS_TITLE_STRING_NUMBER, 'Standard Readings')}</h3>
            <div dangerouslySetInnerHTML={{ __html: standardReadingsHtml }} />
          </section>

          <section className={classes.customReadingsColumn}>
            <h3>{t(ADDITIONAL_READINGS_TITLE_STRING_NUMBER, 'Additional Readings')}</h3>
            <p>{t(ADDITIONAL_READINGS_TEXT_STRING_NUMBER, ADDITIONAL_READINGS_TEXT)}</p>
            <div className={classes.customReadingsOptions}>
              {interests.map((interest) => (
                <label key={interest.key} className={classes.customReadingOption}>
                  <input
                    type="checkbox"
                    checked={selectedInterests.includes(interest.key)}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setSelectedInterests((prev) => prev.includes(interest.key) ? prev : [...prev, interest.key]);
                      } else {
                        setSelectedInterests((prev) => prev.filter((item) => item !== interest.key));
                      }
                    }}
                  />
                  <span>{t(interest.stringNumber, interest.label)}</span>
                </label>
              ))}
              {!interests.length && <span className={classes.empty}>No additional readings configured for this age/language.</span>}
            </div>
          </section>
        </div>
      ) : (
        <div className={classes.reportBody}>
          <div className={classes.headerGrid}>
            <div>
              <div><strong>{t(USER_STRING_NUMBER, 'User')}:</strong> {learner?.firstName || learner?.username || learner?.name || '—'}</div>
              <div><strong>{t(DATE_STRING_NUMBER, 'Date')}:</strong> {getGeneratedDate(report, assessment)}</div>
            </div>
            <div>
              <div><strong className={classes.readyLabel}>{t(AGES_STRING_NUMBER, 'Ages')}:</strong> {getAgeBandDisplay(assessment, report)}</div>
              <div><strong className={classes.readyLabel}>{t(LANGUAGE_STRING_NUMBER, 'Language')}:</strong> {getLanguageDisplay(report, assessment)}</div>
            </div>
          </div>

          <div className={classes.explanatoryRow}>
            {explanatoryAudioUrl && (
              <button
                type="button"
                className={classes.explanatoryAudioButton}
                onClick={() => (mobileRuntime ? dispatchToggleAudio(explanatoryAudioUrl) : toggleAudio(explanatoryAudioUrl))}
                aria-label="Play report explanation audio"
              >
                <img src={explanatoryAudioIcon} alt="" />
              </button>
            )}
            <div
              className={classes.explanatoryText}
              dangerouslySetInnerHTML={{ __html: explanatoryHtml }}
            />
          </div>

          <div className={classes.sectionTitle}>{t(SKILL_RATINGS_STRING_NUMBER, 'Skill Ratings')}</div>
          <div className={classes.skillGrid}>
            {subjectRows.map((subject) => {
              const levelReady = hasLevel(levels[subject]);
              return (
                <div className={classes.skillRow} key={subject}>
                  <span className={levelReady ? classes.readyLabel : undefined}>{t(subjectStringIds?.[subject] || SUBJECT_STRING_NUMBERS[subject], SUBJECT_FALLBACKS[subject])}</span>
                  <strong>{levelReady ? levels[subject] : '—'}</strong>
                  <span className={classes.completedText}>{t(ACTIVITIES_COMPLETED_STRING_NUMBER, 'Activities Completed')}: {getProgressLine(progress, subject)}</span>
                </div>
              );
            })}
          </div>

          {!mobileRuntime && (
            <>
              <div className={classes.sectionTitle}>
                <span className={allSkillsReady ? classes.readyLabel : undefined}>{t(INTERESTS_STRING_NUMBER, 'Interests')}</span>
              </div>
              <div className={classes.interestsGrid}>
                {interests.map((interest) => (
                  <label key={interest.key} className={classes.interestItem}>
                    <input
                      type="checkbox"
                      checked={selectedInterests.includes(interest.key)}
                      disabled={!allSkillsReady || (!selectedInterests.includes(interest.key) && selectedInterests.length >= 3)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedInterests((prev) => prev.includes(interest.key) ? prev : [...prev, interest.key].slice(0, 3));
                        } else {
                          setSelectedInterests((prev) => prev.filter((item) => item !== interest.key));
                        }
                      }}
                    />
                    <span>{t(interest.stringNumber, interest.label)}</span>
                  </label>
                ))}
                {!interests.length && <span className={classes.empty}>No interests configured for this age/language.</span>}
              </div>
            </>
          )}
        </div>
      )}
    </GameModal>
  );
};

DesktopAssessmentReportModal.propTypes = {
  assessment: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onGetCustomContent: PropTypes.func,
  translate: PropTypes.func,
  learner: PropTypes.object,
  strings: PropTypes.array,
  solutionLocale: PropTypes.object,
  getStringAudio: PropTypes.func.isRequired,
  audioSrc: PropTypes.string,
  isPlaying: PropTypes.bool,
  dispatchToggleAudio: PropTypes.func.isRequired,
  suggestionsOnly: PropTypes.bool,
  fullProductReportStrings: PropTypes.object,
};

const mapStateToProps = ({ flinkPlay, status, audio, common }, ownProps) => {
  const report = getReportFromAssessment(ownProps?.assessment);
  const reportLanguageCode = getLanguageKey(report, ownProps?.assessment);
  const reportTranslate = reportLanguageCode && Array.isArray(common.strings)
    ? getTranslateFunc(common.strings, reportLanguageCode)
    : null;

  return {
    translate: reportTranslate || flinkPlay.solutionTranslate || status.translate,
    learner: flinkPlay.learner,
    strings: common.strings,
    solutionLocale: flinkPlay.solutionLocale || status.lang,
    audioSrc: audio.audioSrc,
    isPlaying: audio.isPlaying,
    subjectStringIds: common.config?.customContentSettings?.subjectStringIds || {},
    readingInterests: common.config?.customContentSettings?.readingInterests || [],
    fullProductReportStrings: common.config?.customContentSettings?.fullProductReportStrings || {},
  };
};

export default compose(
  withGetStringAudio,
  connect(mapStateToProps, { dispatchToggleAudio: toggleAudio }),
)(DesktopAssessmentReportModal);
